import { useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';

import { getUpdGetDocumentQueryKey, useUpdResolutions } from '@/api/generated/endpoints/upd/upd';
import type { ResolutionsInResolutionsItem, UpdDocumentDetail } from '@/api/generated/model';
import { ApiProblem } from '@/api/http';
import { problemMessage } from '@/i18n/problem';
import { invalidatePath } from '@/lib/queryCache';

/**
 * Одно решение по одной проблеме УПД. Сервер сразу сопоставляет документ заново:
 * в ответе — новые проблемы (выбрали весовой товар → спросит вес штуки) или черновик.
 */
export function useApplyResolution(doc: UpdDocumentDetail) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const mutation = useUpdResolutions({
    mutation: {
      onSuccess: async (next) => {
        queryClient.setQueryData(getUpdGetDocumentQueryKey(doc.id), next);
        await Promise.all([
          invalidatePath(queryClient, '/api/v1/upd-documents'),
          invalidatePath(queryClient, '/api/v1/shipments'),
          invalidatePath(queryClient, '/api/v1/clients'),
          invalidatePath(queryClient, '/api/v1/products'),
        ]);
        void message.success(
          next.status === 'draft_created' ? t('resolve.draftCreated') : t('resolve.applied'),
        );
      },
      onError: async (e) => {
        if (e instanceof ApiProblem && e.code === 'version_conflict') {
          await invalidatePath(queryClient, '/api/v1/upd-documents');
        }
      },
    },
  });
  return {
    apply: (resolution: ResolutionsInResolutionsItem) => {
      mutation.mutate({ docId: doc.id, data: { version: doc.version, resolutions: [resolution] } });
    },
    pending: mutation.isPending,
    error: mutation.error,
    errorText: mutation.error ? problemMessage(mutation.error) : null,
    reset: mutation.reset,
  };
}
